'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { readChromeSession } from './session.js'

const ENDPOINT = 'https://unitycosmos.com/api/bug-report'
const STYLE_ID = 'uc-bug-style'

const COPY = {
  es: {
    label: 'Falla',
    open: 'Reportar una falla',
    title: 'Reportar una falla',
    hint: 'Qué se rompió. Esta página va incluida.',
    placeholder: 'Qué pasó, y qué esperabas',
    email: 'Tu correo, si quieres respuesta',
    send: 'Enviar',
    sending: 'Enviando…',
    cancel: 'Cerrar',
    ok: 'Listo. Lo revisamos.',
    err: 'No salió. Inténtalo otra vez.',
  },
  en: {
    label: 'Bug',
    open: 'Report a problem',
    title: 'Report a problem',
    hint: 'What broke. This page is included.',
    placeholder: 'What happened, and what you expected',
    email: 'Your email, if you want a reply',
    send: 'Send',
    sending: 'Sending…',
    cancel: 'Close',
    ok: 'Got it. We’ll look.',
    err: 'That didn’t send. Try again.',
  },
}

const SECRET_QUERY = [
  'uc_receipt',
  'unity_handoff',
  'token',
  'access_token',
  'id_token',
  'code',
  'receipt',
]

function pageLang() {
  if (typeof document === 'undefined') return 'es'
  try {
    const hub = localStorage.getItem('unity-cosmos-lang')
    if (hub === 'es' || hub === 'en') return hub
    const diary = localStorage.getItem('app_language')
    if (diary === 'es' || diary === 'en') return diary
  } catch {
    /* ignore */
  }
  const html = (document.documentElement.lang || '').toLowerCase()
  if (html.startsWith('es')) return 'es'
  if (html.startsWith('en')) return 'en'
  const nav = (typeof navigator !== 'undefined' ? navigator.language || '' : '').toLowerCase()
  return nav.startsWith('en') ? 'en' : 'es'
}

function safePageUrl() {
  try {
    const url = new URL(window.location.href)
    for (const key of SECRET_QUERY) url.searchParams.delete(key)
    return url.toString().slice(0, 500)
  } catch {
    return ''
  }
}

function reportEndpoint() {
  const host = window.location.hostname
  if (host === 'unitycosmos.com' || host.endsWith('.unitycosmos.com')) return '/api/bug-report'
  return ENDPOINT
}

function useEmbedCovers(enabled) {
  const [covers, setCovers] = useState(enabled)
  useEffect(() => {
    if (!enabled) {
      setCovers(false)
      return undefined
    }
    const check = () => setCovers(Boolean(document.querySelector('iframe.world-canvas__frame')))
    check()
    const obs = new MutationObserver(check)
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [enabled])
  return covers
}

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
.uc-bug{position:fixed;z-index:96;right:0.85rem;bottom:calc(0.85rem + env(safe-area-inset-bottom,0px));display:flex;flex-direction:column;align-items:flex-end;gap:0.55rem;font-family:system-ui,-apple-system,sans-serif;pointer-events:none}
.uc-bug--lift{bottom:calc(4.85rem + env(safe-area-inset-bottom,0px))}
.uc-bug--subtle{right:0.55rem;bottom:calc(0.55rem + env(safe-area-inset-bottom,0px));opacity:0.42;transition:opacity .18s ease}
.uc-bug--subtle:hover,.uc-bug--subtle:focus-within{opacity:0.92}
.uc-bug>*{pointer-events:auto}
@media(min-width:900px){.uc-bug,.uc-bug--lift{bottom:1.15rem}.uc-bug--subtle{bottom:0.75rem;right:0.65rem}}
.uc-bug__btn{display:inline-flex;align-items:center;gap:0.42rem;height:2.55rem;padding:0 0.9rem 0 0.72rem;border-radius:999px;border:1px solid rgba(240,194,122,0.78);background:rgba(18,15,12,0.82);color:#f7f1e8;box-shadow:0 10px 28px rgba(0,0,0,0.34);backdrop-filter:blur(12px);font-size:0.84rem;font-weight:650;letter-spacing:0.01em;cursor:pointer}
.uc-bug__btn--subtle{width:2.15rem;height:2.15rem;padding:0;justify-content:center;border-radius:999px;border:1px solid rgba(148,163,184,0.28);background:rgba(12,14,18,0.72);color:#cbd5e1;box-shadow:0 4px 14px rgba(0,0,0,0.22);font-size:0;gap:0}
.uc-bug__btn svg{width:15px;height:15px;color:#f0c27a;flex-shrink:0}
.uc-bug__btn--subtle svg{width:17px;height:17px;color:#94a3b8}
.uc-bug__btn:hover,.uc-bug__btn[aria-expanded="true"]{background:rgba(36,28,18,0.96);border-color:#f0c27a}
.uc-bug__btn--subtle:hover,.uc-bug__btn--subtle[aria-expanded="true"]{background:rgba(22,26,34,0.92);border-color:rgba(148,163,184,0.45);color:#e2e8f0}
.uc-bug__btn--subtle:hover svg,.uc-bug__btn--subtle[aria-expanded="true"] svg{color:#e2e8f0}
.uc-bug__btn:focus-visible{outline:2px solid #f0c27a;outline-offset:3px}
.uc-bug__btn--subtle:focus-visible{outline-color:rgba(148,163,184,0.75)}
.uc-bug__panel{width:min(22rem,calc(100vw - 1.5rem));padding:0.9rem 0.9rem 0.8rem;border-radius:16px;border:1px solid rgba(240,194,122,0.45);background:rgba(16,14,12,0.94);color:#f7f1e8;box-shadow:0 18px 48px rgba(0,0,0,0.42);backdrop-filter:blur(16px)}
.uc-bug__panel h2{margin:0;font-size:0.98rem;font-weight:680;letter-spacing:-0.01em}
.uc-bug__hint{margin:0.28rem 0 0.7rem;color:rgba(247,241,232,0.68);font-size:0.78rem;line-height:1.35}
.uc-bug__panel textarea,.uc-bug__panel input{width:100%;box-sizing:border-box;border-radius:10px;border:1px solid rgba(247,241,232,0.16);background:rgba(255,255,255,0.04);color:inherit;font:inherit;font-size:0.86rem}
.uc-bug__panel textarea{min-height:6.2rem;resize:vertical;padding:0.6rem 0.65rem}
.uc-bug__panel input{margin-top:0.45rem;height:2.35rem;padding:0 0.65rem}
.uc-bug__panel textarea:focus,.uc-bug__panel input:focus{outline:2px solid rgba(240,194,122,0.7);border-color:transparent}
.uc-bug__hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}
.uc-bug__row{display:flex;justify-content:flex-end;gap:0.4rem;margin-top:0.65rem}
.uc-bug__ghost,.uc-bug__send{height:2.15rem;padding:0 0.8rem;border-radius:999px;font:inherit;font-size:0.8rem;font-weight:650;cursor:pointer}
.uc-bug__ghost{border:1px solid rgba(247,241,232,0.18);background:transparent;color:rgba(247,241,232,0.82)}
.uc-bug__send{border:0;background:#f0c27a;color:#1a140c}
.uc-bug__send:disabled,.uc-bug__ghost:disabled{opacity:0.55;cursor:default}
.uc-bug__note{margin:0.55rem 0 0;font-size:0.78rem;color:#f0c27a}
.uc-bug__note--err{color:#ffb4a8}
.uc-bug-item{display:flex;align-items:center;gap:0.5rem;width:100%;text-align:left;cursor:pointer}
.uc-bug-item svg{width:14px;height:14px;flex-shrink:0}
`
  document.head.appendChild(style)
}

/** Speech bubble + exclamation — reads as “report” without a flag. */
function ReportIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M2.5 3.2a1.2 1.2 0 0 1 1.2-1.2h8.6a1.2 1.2 0 0 1 1.2 1.2v5.4a1.2 1.2 0 0 1-1.2 1.2H6.2L3.4 12.8V3.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path d="M8 4.6v3.2M8 9.1v.35" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
    </svg>
  )
}

export function BugReportButton({ app, yieldToEmbed = false, lift = false, variant = 'subtle', itemClassName = '', hidden = false }) {
  const covers = useEmbedCovers(yieldToEmbed)
  const [ready, setReady] = useState(false)
  const [open, setOpen] = useState(false)
  const [lang, setLang] = useState('es')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [status, setStatus] = useState('idle')
  const [note, setNote] = useState('')
  const textRef = useRef(null)
  const buttonRef = useRef(null)
  const panelRef = useRef(null)
  const openedOnce = useRef(false)
  const titleId = useId()
  const copy = COPY[lang] || COPY.es

  useEffect(() => {
    const onOpen = (event) => {
      if (event?.detail?.app && event.detail.app !== app) return
      setLang(pageLang())
      setNote('')
      setStatus('idle')
      setOpen(true)
    }
    window.addEventListener('uc-bug-open', onOpen)
    return () => window.removeEventListener('uc-bug-open', onOpen)
  }, [app])

  useEffect(() => {
    setReady(true)
    ensureStyle()
    const apply = () => setLang(pageLang())
    apply()
    const obs = new MutationObserver(apply)
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] })
    const later = window.setTimeout(apply, 500)
    const session = readChromeSession()
    const known = typeof session?.email === 'string' ? session.email : ''
    if (known) setEmail(known)
    return () => {
      obs.disconnect()
      window.clearTimeout(later)
    }
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const id = window.setTimeout(() => textRef.current?.focus(), 30)
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointer = (event) => {
      const panel = panelRef.current
      const button = buttonRef.current
      if (!panel || panel.contains(event.target) || button?.contains(event.target)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      openedOnce.current = true
      return
    }
    if (openedOnce.current) buttonRef.current?.focus()
  }, [open])

  if (!hidden && variant !== 'menuitem' && (!ready || covers)) return null

  const close = () => setOpen(false)

  const submit = async (event) => {
    event.preventDefault()
    const text = message.trim()
    if (text.length < 8 || status === 'sending') return
    setStatus('sending')
    setNote('')
    const session = readChromeSession()
    try {
      const res = await fetch(reportEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app,
          message: text,
          page: safePageUrl(),
          email: email.trim(),
          name: typeof session?.name === 'string' ? session.name : '',
          company,
        }),
      })
      if (!res.ok) throw new Error('send')
      setStatus('sent')
      setNote(copy.ok)
      setMessage('')
      window.setTimeout(() => {
        setOpen(false)
        setStatus('idle')
        setNote('')
      }, 1600)
    } catch {
      setStatus('error')
      setNote(copy.err)
    }
  }

  const openPanel = () => {
    setLang(pageLang())
    setNote('')
    setStatus('idle')
    setOpen(true)
  }

  const panel = open ? (
    <form
      ref={panelRef}
      className="uc-bug__panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onSubmit={submit}
    >
      <h2 id={titleId}>{copy.title}</h2>
      <p className="uc-bug__hint">{copy.hint}</p>
      <textarea
        ref={textRef}
        value={message}
        maxLength={4000}
        required
        placeholder={copy.placeholder}
        aria-label={copy.placeholder}
        onChange={(event) => setMessage(event.target.value)}
      />
      <input
        type="email"
        inputMode="email"
        autoComplete="email"
        value={email}
        maxLength={120}
        placeholder={copy.email}
        aria-label={copy.email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <input
        className="uc-bug__hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={company}
        onChange={(event) => setCompany(event.target.value)}
      />
      {note ? <p className={status === 'error' ? 'uc-bug__note uc-bug__note--err' : 'uc-bug__note'}>{note}</p> : null}
      <div className="uc-bug__row">
        <button type="button" className="uc-bug__ghost" onClick={close}>
          {copy.cancel}
        </button>
        <button type="submit" className="uc-bug__send" disabled={status === 'sending' || message.trim().length < 8}>
          {status === 'sending' ? copy.sending : copy.send}
        </button>
      </div>
    </form>
  ) : null

  if (hidden) {
    if (!open) return null
    return createPortal(<div className="uc-bug">{panel}</div>, document.body)
  }

  if (variant === 'menuitem') {
    return (
      <>
        <button
          ref={buttonRef}
          type="button"
          role="menuitem"
          className={['uc-bug-item', itemClassName].filter(Boolean).join(' ')}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            openPanel()
          }}
        >
          <ReportIcon />
          {copy.open}
        </button>
        {panel ? createPortal(<div className="uc-bug">{panel}</div>, document.body) : null}
      </>
    )
  }

  const subtle = variant === 'subtle' || (variant !== 'pill' && variant !== 'menuitem')
  const rootClass = [
    'uc-bug',
    lift ? 'uc-bug--lift' : '',
    subtle ? 'uc-bug--subtle' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return createPortal(
    <div className={rootClass} data-uc-bug={app}>
      {panel}
      <button
        ref={buttonRef}
        type="button"
        className={subtle ? 'uc-bug__btn uc-bug__btn--subtle' : 'uc-bug__btn'}
        aria-expanded={open}
        aria-label={copy.open}
        title={copy.open}
        onClick={() => (open ? close() : openPanel())}
      >
        <ReportIcon />
        {subtle ? null : copy.label}
      </button>
    </div>,
    document.body,
  )
}
